import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { BellOutline } from '@lyearn/icons';
import { SidebarEmptyState } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Divider, Loader as BaseLoader, Stack } from '@lyearn/ui';
import Loader from '@/components/Loader';
import VirtualizedList from '@/components/VirtualizedList';

import NotificationItem from './NotificationItem';
import { emptyDescription, messageItem, messageItemMobile } from './styles';
import { NotificationItemsProps } from './types';

export const getEmptyStateOverrides = (isMobile: boolean) => ({
  description: {
    variant: 'label-02',
    color: 'text-text-placeholder',
    className: cn('text-center', emptyDescription(isMobile)),
  },
  icon: { className: 'mb-16 text-icon-placeholder xs:w-32 xs:h-32' },
});

const NotificationList: React.FC<NotificationItemsProps> = (props) => {
  const { t } = useTranslation();
  const { isMobile } = useScreen();
  const {
    fetchMore,
    isFetchingMore,
    userNotifications,
    initialFetching,
    onNotificationItemClick,
    onClickBadgeType,
    onClickPointsAwarded,
  } = props;

  const itemContent = React.useCallback(
    (index: number) => {
      return (
        <Stack className="mt-7" direction="col">
          <Divider className={cn(index === 0 ? 'hidden' : '')} orientation="horizontal" />
          <NotificationItem
            index={index}
            userNotification={userNotifications?.[index]}
            onClickBadgeType={onClickBadgeType}
            onClickPointsAwarded={onClickPointsAwarded}
            onNotificationItemClick={onNotificationItemClick}
          />
        </Stack>
      );
    },
    [onClickBadgeType, onClickPointsAwarded, onNotificationItemClick, userNotifications],
  );

  const ListFooterComponent = React.useMemo<JSX.Element | undefined>(() => {
    if (!isFetchingMore) return;
    return (
      <Stack alignItems="center" justifyContent="center">
        <BaseLoader
          arcColorToken="icon-active"
          circleColorToken="icon-placeholder"
          size={40}
          style={{ height: 40, width: 40 }}
        />
      </Stack>
    );
  }, [isFetchingMore]);

  if (initialFetching) {
    return (
      <Stack alignItems="center" className="flex-1 py-12" justifyContent="center">
        <Loader showLabel={false} />
      </Stack>
    );
  }

  if (!userNotifications?.length) {
    return (
      <SidebarEmptyState
        Icon={BellOutline}
        className="h-full w-full"
        description={t('Common:Notifications.EmptyDescription')}
      />
    );
  }

  return (
    <VirtualizedList
      ListFooterComponent={ListFooterComponent}
      className={cn(
        'flex w-full flex-1',
        isMobile ? messageItemMobile : messageItem,
        props.className,
      )}
      contentContainerClassName="mt-4 xs:mt-10 ios:insert-padding-bottom-screen android:pb-12"
      data={userNotifications}
      endReached={fetchMore}
      itemContent={itemContent}
    />
  );
};

export default NotificationList;
